// buildFreewheelSlackMessage.js
//
// Turns a FreeWheel CSAI lifecycle session into a Slack-ready bug report the QA
// reviewer can copy and paste to the app team. Output is Slack mrkdwn: *bold*,
// `code`, `•` bullets, `>` blockquote, and severity emoji. Pure + null-safe.
//
// Shape consumed: the `flavor:'freewheel'` session built by buildVideoSessions —
// session.freewheel.{networkId,profile,siteSectionId,contentAssetId,omidPartner,
// omidVersion,usPrivacy,deliveryMode,prd}, session.gamVideo.row,
// session.gamFilled, session.pods, session.videoDemand, session.timestamp.

const SEV = {
  fail: { emoji: '🔴', label: 'Blocking (must fix)' },
  warn: { emoji: '🟠', label: 'Warnings' },
  info: { emoji: 'ℹ️', label: 'Notes' },
};

// "516428:tmz_ioshandheld_vod" → "TMZ · iOS handheld (VOD)"
function platformLabel(profile) {
  if (!profile) return null;
  const tail = String(profile).split(':').pop();
  const m = /^([a-z0-9]+)_(ios|android|tvos|web)(handheld|tablet|phone|tv)?_(vod|live)$/i.exec(tail || '');
  if (!m) return tail || null;
  const [, app, os, device, mode] = m;
  const osLabel = { ios: 'iOS', android: 'Android', tvos: 'tvOS', web: 'Web' }[os.toLowerCase()] || os;
  return `${app.toUpperCase()} · ${osLabel}${device ? ' ' + device : ''} (${mode.toUpperCase()})`;
}

function fmtTime(ts) {
  if (!ts) return null;
  try { return new Date(ts).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'; }
  catch { return null; }
}

// Trim a long ad-tag URL so the Slack message stays readable but is still repro-able.
function trimUrl(url, max = 160) {
  if (!url) return null;
  return url.length > max ? url.slice(0, max) + '…' : url;
}

export function buildFreewheelSlackMessage(session) {
  if (!session || session.flavor !== 'freewheel') return '';
  const fw = session.freewheel || {};
  const prd = fw.prd || null;
  const row = session.gamVideo?.row || null;

  const status = prd?.status || (session.overallStatus === 'fail' ? 'fail' : session.overallStatus === 'warn' ? 'warn' : 'pass');
  const headEmoji = status === 'fail' ? '🔴' : status === 'warn' ? '🟠' : '✅';
  const lines = [];

  lines.push(`${headEmoji} *FreeWheel CSAI — PRD conformance: ${status.toUpperCase()}*`);
  const sub = [platformLabel(fw.profile), fmtTime(session.timestamp)].filter(Boolean).join(' · ');
  if (sub) lines.push(`_${sub}_`);
  lines.push('');

  // Request context
  lines.push('*Request*');
  const ctx = [];
  if (fw.networkId) ctx.push(`Network: \`${fw.networkId}\``);
  if (fw.profile) ctx.push(`Profile: \`${fw.profile}\``);
  if (ctx.length) lines.push('• ' + ctx.join('  ·  '));
  const ctx2 = [];
  if (fw.contentAssetId) ctx2.push(`Content (caid): \`${fw.contentAssetId}\``);
  const fill = session.gamFilled === true
    ? `✅ ${session.pods?.breakCount != null ? `${session.pods.breakCount} pod${session.pods.breakCount !== 1 ? 's' : ''}` : 'filled'}`
    : session.gamFilled === false ? '⚠️ no fill' : '—';
  ctx2.push(`Fill: ${fill}`);
  lines.push('• ' + ctx2.join('  ·  '));
  const ctx3 = [];
  if (fw.omidPartner) ctx3.push(`OMID: \`${[fw.omidPartner, fw.omidVersion].filter(Boolean).join(' ')}\``);
  if (session.videoDemand) ctx3.push(`SpringServe: ${session.videoDemand.status.replace('_', '-')} (inferred)`);
  if (ctx3.length) lines.push('• ' + ctx3.join('  ·  '));

  // Findings grouped by severity (skip the pass case with no findings)
  const findings = (prd?.findings || []);
  for (const sev of ['fail', 'warn', 'info']) {
    const group = findings.filter((f) => f.level === sev);
    if (!group.length) continue;
    lines.push('');
    lines.push(`*${SEV[sev].emoji} ${SEV[sev].label}*`);
    for (const f of group) lines.push(`• ${f.msg}`);
  }

  if (prd && status === 'pass') {
    lines.push('');
    lines.push(`✅ Tag conforms to ${prd.spec}.`);
  }

  // Repro + provenance
  lines.push('');
  const u = trimUrl(row?.url);
  if (u) lines.push(`>Ad tag: \`${u}\``);
  if (prd?.spec) lines.push(`_Spec: ${prd.spec}_`);
  lines.push(`_Captured with fx-validator${session.timestamp ? ' · ' + (fmtTime(session.timestamp) || '') : ''}_`);

  return lines.join('\n');
}

export default buildFreewheelSlackMessage;
